import React from 'react';
import { motion } from 'framer-motion';

const TaxCalculator = ({ 
  subtotal = 0, 
  taxRate = 0, 
  taxType = 'percentage', // 'percentage' or 'fixed'
  onTaxRateChange,
  onTaxTypeChange,
  currency = 'USD'
}) => {
  const calculateTaxAmount = () => {
    if (taxType === 'percentage') {
      return (subtotal * (parseFloat(taxRate) || 0)) / 100;
    } else {
      return parseFloat(taxRate) || 0;
    }
  };

  const taxAmount = calculateTaxAmount();
  const total = subtotal + taxAmount;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-black border border-gray-200 dark:border-[#1A1A1A] rounded-2xl p-6 space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Tax & Totals</h3>
          <p className="text-sm text-gray-500 dark:text-[#666666] mt-1">
            Configure tax rates and view final amounts
          </p>
        </div>
        
        {/* Tax Type Toggle */}
        <div className="flex items-center bg-gray-100 dark:bg-[#1A1A1A] rounded-xl p-1">
          <button
            type="button"
            onClick={() => onTaxTypeChange('percentage')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
              taxType === 'percentage'
                ? 'bg-white dark:bg-black text-gray-900 dark:text-white shadow-sm border dark:border-[#2A2A2A]'
                : 'text-gray-600 dark:text-[#666666] hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Percentage
          </button>
          <button
            type="button"
            onClick={() => onTaxTypeChange('fixed')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
              taxType === 'fixed'
                ? 'bg-white dark:bg-black text-gray-900 dark:text-white shadow-sm border dark:border-[#2A2A2A]'
                : 'text-gray-600 dark:text-[#666666] hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Fixed Amount
          </button>
        </div>
      </div>

      {/* Tax Input */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
            Tax {taxType === 'percentage' ? 'Rate' : 'Amount'}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-4">
              <span className="text-gray-500 dark:text-[#666666] font-medium">
                {taxType === 'percentage' ? '%' : currency}
              </span>
            </div>
            <input
              type="number"
              min="0"
              step={taxType === 'percentage' ? '0.01' : '0.01'}
              max={taxType === 'percentage' ? '100' : undefined}
              value={taxRate}
              onChange={(e) => {
                const value = e.target.value;
                // Prevent leading zeros except for decimal values
                if (value.length > 1 && value.startsWith('0') && !value.startsWith('0.')) {
                  onTaxRateChange(value.substring(1));
                } else {
                  onTaxRateChange(value);
                }
              }}
              onFocus={(e) => {
                // Select all text when focused, making it easy to replace
                e.target.select();
              }}
              onWheel={(e) => {
                // Prevent mouse wheel from changing value
                e.preventDefault();
                e.target.blur();
              }}
              className="w-full pl-16 pr-4 py-4 bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-xl text-lg font-medium text-gray-900 dark:text-white focus:ring-2 focus:ring-brand/20 focus:border-brand transition-all hover:border-gray-300 dark:hover:border-[#666666] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              placeholder={taxType === 'percentage' ? '0' : '0.00'}
              autoComplete="off"
            />
          </div>
          {taxType === 'percentage' && (
            <p className="text-xs text-gray-500 dark:text-[#666666] mt-2">
              Enter percentage (e.g., 8.25 for 8.25%)
            </p>
          )}
        </div>
      </div>

      {/* Calculation Breakdown */}
      <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-[#1A1A1A]">
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-600 dark:text-[#666666]">Subtotal</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {currency} {subtotal.toFixed(2)}
            </span>
          </div>
          
          {taxAmount > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="flex justify-between items-center"
            >
              <span className="text-gray-600 dark:text-[#666666]">
                Tax {taxType === 'percentage' ? `(${taxRate}%)` : ''}
              </span>
              <span className="font-medium text-gray-900 dark:text-white">
                {currency} {taxAmount.toFixed(2)}
              </span>
            </motion.div>
          )}
        </div>
        
        {/* Total */}
        <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-[#1A1A1A]">
          <span className="text-xl font-semibold text-gray-900 dark:text-white">Total</span>
          <span className="text-2xl font-bold text-gray-900 dark:text-white">
            {currency} {total.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Calculation Helper */}
      {taxType === 'percentage' && taxRate > 0 && subtotal > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/50 rounded-xl p-4"
        >
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-blue-500 dark:text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                Tax calculation
              </p>
              <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                {currency} {subtotal.toFixed(2)} × {taxRate}% = {currency} {taxAmount.toFixed(2)}
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default TaxCalculator; 